import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  Center,
  Image,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const DEFAULT_PASSWORD = "!@rhoonart";
const encodeBase64 = (str) => btoa(unescape(encodeURIComponent(str)));

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetPhone, setResetPhone] = useState("");
  const [resetStep, setResetStep] = useState(0); // 0: 입력, 1: 인증번호 입력
  const [resetCode, setResetCode] = useState("");
  const [resetPhoneLoading, setResetPhoneLoading] = useState(false);
  const [resetVerifyLoading, setResetVerifyLoading] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [foundPhone, setFoundPhone] = useState("");
  const [phoneFetchLoading, setPhoneFetchLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();
  const toast = useToast();

  // 뒤로가기 시 무조건 홈으로 이동
  useEffect(() => {
    const handlePopState = () => {
      navigate("/", { replace: true });
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // 비밀번호 찾기 Modal 열기
  const openResetModal = () => {
    setResetModalOpen(true);
    setResetStep(0);
    setResetEmail("");
    setMaskedPhone("");
    setFoundPhone("");
    setResetCode("");
  };

  // 이메일 입력 후 휴대폰 번호 조회
  const handleFindPhone = async () => {
    if (!resetEmail) {
      toast({
        title: "이메일을 입력해주세요.",
        status: "warning",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    setPhoneFetchLoading(true);
    try {
      const { data: userData, error: userError } = await supabase
        .from("user")
        .select("phone_number")
        .eq("email", resetEmail.trim())
        .maybeSingle();
      if (userError || !userData || !userData.phone_number) {
        toast({
          title: "등록되지 않은 이메일입니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setResetModalOpen(false);
        return;
      }

      let phone = userData.phone_number;
      if (userData.phone_number[0] === "0") {
        setFoundPhone(userData.phone_number);
      } else {
        setFoundPhone("0" + userData.phone_number);
        phone = "0" + userData.phone_number;
      }
      // 마스킹 처리 (뒷 4자리 *로)
      const masked =
        phone.length >= 4 ? phone.slice(0, phone.length - 4) + "****" : "****";
      setMaskedPhone(masked);
      setResetStep(1);
    } catch (error) {
      toast({
        title: "오류",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      setResetModalOpen(false);
    } finally {
      setPhoneFetchLoading(false);
    }
  };

  // 인증번호 발송
  const handleSendOtp = async () => {
    if (!foundPhone) return;
    setResetPhoneLoading(true);
    try {
      const response = await fetch(
        "https://hupnidxelbhaluxrsygd.supabase.co/functions/v1/send-sms",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phone: foundPhone }),
        }
      );
      if (!response.ok) throw new Error("문자 발송에 실패했습니다.");
      toast({
        title: "인증번호가 발송되었습니다.",
        description: "휴대폰으로 전송된 인증번호를 입력해주세요.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      setResetStep(2);
    } catch (error) {
      toast({
        title: "인증번호 발송 실패",
        description: error.message || "SMS 발송에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setResetPhoneLoading(false);
    }
  };

  // 인증번호 확인 및 비밀번호 재설정 메일 발송
  const handleVerifyOtpAndSendMail = async () => {
    setResetVerifyLoading(true);
    try {
      const res = await fetch(
        "https://hupnidxelbhaluxrsygd.supabase.co/functions/v1/verify-sms",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phone: foundPhone, code: resetCode }),
        }
      );
      const result = await res.json();
      if (!result.success) throw new Error("인증번호가 올바르지 않습니다.");

      // 인증 성공 시 비밀번호 재설정 메일 발송
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        resetEmail.trim(),
        { redirectTo: `${window.location.origin}/reset-password` }
      );
      if (resetError) throw resetError;

      toast({
        title: "비밀번호 재설정 메일이 전송되었습니다.",
        description: "이메일을 확인해주세요.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      setResetModalOpen(false);
    } catch (error) {
      toast({
        title: "인증 실패",
        description: error.message || "인증에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setResetVerifyLoading(false);
    }
  };

  // 비밀번호 유효성 검사
  const validatePassword = (password) => {
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>[\]\\/~`';_+=-]/.test(
      password
    );

    if (!hasLowerCase || !hasNumber || !hasSpecialChar) {
      const errors = [];
      if (!hasLowerCase) errors.push("소문자");
      if (!hasNumber) errors.push("숫자");
      if (!hasSpecialChar) errors.push("특수기호");
      return `비밀번호는 ${errors.join(", ")}를 포함해야 합니다.`;
    }
    return "";
  };

  // 로그인
  const handleLogin = async (e) => {
    e.preventDefault();
    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      toast({
        title: "비밀번호 형식 오류",
        description: passwordValidationError,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    setPasswordError("");
    setLoading(true);

    try {
      // 1. auth에서 로그인 시도
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (authData?.user) {
        toast({
          title: "로그인 성공",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top",
        });
        navigate("/");
        return;
      }

      // 2. 로그인 실패 시
      if (authError) {
        // user 테이블에서 확인
        const { data: userData, error: userError } = await supabase
          .from("user")
          .select("email, is_migration")
          .eq("email", email.trim())
          .maybeSingle();

        if (userError || !userData) {
          toast({
            title: "로그인 실패",
            description: "등록되지 않은 이메일입니다.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          return;
        }

        // is_migration이 true면 비밀번호 오류 안내
        if (userData.is_migration === true) {
          toast({
            title: "로그인 실패",
            description: "이메일 또는 비밀번호가 올바르지 않습니다.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          return;
        }

        // is_migration이 false(또는 null/undefined)이면
        // 1. auth에 계정 생성
        const { error: signUpError } = await supabase.auth.signUp({
          email: email.trim(),
          password: password,
          options: {
            emailRedirectTo: `${window.location.origin}/login`,
          },
        });
        if (signUpError) {
          toast({
            title: "사용자 등록 실패",
            description:
              "사용자 등록 중 오류가 발생했습니다. 고객센터로 문의해주세요.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          return;
        }
        // // 2. 비밀번호 재설정 메일 발송
        // const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        //   email.trim(),
        //   { redirectTo: `${window.location.origin}/reset-password` }
        // );
        // if (resetError) {
        //   toast({
        //     title: "비밀번호 재설정 실패",
        //     description: "비밀번호 재설정 이메일 전송에 실패했습니다.",
        //     status: "error",
        //     duration: 3000,
        //     isClosable: true,
        //     position: "top",
        //   });
        //   return;
        // }

        // 3. is_migration true로 업데이트
        await supabase
          .from("user")
          .update({ is_migration: true })
          .eq("email", email.trim());

        navigate("/");
        // 4. 비밀번호 재설정 안내 후 로그아웃 처리
        // toast({
        //   title: "비밀번호 재설정 필요",
        //   description:
        //     "비밀번호 재설정 메일이 전송되었습니다. 이메일을 확인해주세요. (스팸함 확인 바랍니다.)",
        //   status: "warning",
        //   duration: 6000,
        //   isClosable: true,
        //   position: "top",
        // });
        // return;
      }
    } catch (error) {
      toast({
        title: "로그인 실패",
        description: error.message || "로그인 중 오류가 발생했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="container.sm" py={10} position="relative" minH="100vh">
      {/* 홈으로 가기 버튼 - 오른쪽 상단 고정 */}
      <Button
        colorScheme="gray"
        variant="ghost"
        size="md"
        onClick={() => navigate("/")}
        position="absolute"
        top="24px"
        right="32px"
        borderRadius="full"
        boxShadow="sm"
        fontWeight="bold"
        _hover={{ bg: "gray.200" }}
        zIndex={10}
      >
        홈으로 가기
      </Button>
      {/* 플플 로고 중앙 정렬, 크게 */}
      <Center flexDirection="column" mb={8} mt={12}>
        <Image src="/images/plpl.png" alt="플플 로고" boxSize="220px" mb={4} />
      </Center>
      <Box bg="white" p={10} borderRadius="2xl" boxShadow="2xl" mt={4}>
        <form onSubmit={handleLogin}>
          <Stack spacing={6}>
            <FormControl isRequired>
              <FormLabel>이메일</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="lg"
                borderRadius="xl"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>비밀번호</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(validatePassword(e.target.value));
                }}
                size="lg"
                borderRadius="xl"
                isInvalid={!!passwordError}
              />
              {passwordError && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {passwordError}
                </Text>
              )}
            </FormControl>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              w="100%"
              size="lg"
              borderRadius="xl"
              fontWeight="bold"
            >
              로그인
            </Button>
            <Button
              variant="link"
              colorScheme="blue"
              onClick={openResetModal}
              w="100%"
              size="lg"
            >
              비밀번호 찾기
            </Button>
            <Text textAlign="center">
              계정이 없으신가요?{" "}
              <Button
                variant="link"
                colorScheme="blue"
                onClick={() => navigate("/signup")}
                size="lg"
              >
                회원가입
              </Button>
            </Text>
          </Stack>
        </form>
      </Box>
      {/* 비밀번호 찾기 Modal */}
      <Modal
        isOpen={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxW="sm">
          <ModalHeader>비밀번호 찾기</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {resetStep === 0 && (
                <>
                  <FormControl isRequired>
                    <FormLabel>이메일</FormLabel>
                    <Input
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="가입한 이메일을 입력하세요"
                    />
                  </FormControl>
                  <Button
                    colorScheme="blue"
                    onClick={handleFindPhone}
                    isLoading={phoneFetchLoading}
                  >
                    휴대폰번호 확인
                  </Button>
                </>
              )}
              {resetStep === 1 && (
                <>
                  <Text>
                    가입된 휴대폰번호: <b>{maskedPhone}</b>
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={handleSendOtp}
                    isLoading={resetPhoneLoading}
                  >
                    인증번호 발송
                  </Button>
                </>
              )}
              {resetStep === 2 && (
                <>
                  <Text>
                    가입된 휴대폰번호: <b>{maskedPhone}</b>
                  </Text>
                  <Input
                    placeholder="인증번호 6자리"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                  />
                  <Button
                    colorScheme="blue"
                    onClick={handleVerifyOtpAndSendMail}
                    isLoading={resetVerifyLoading}
                  >
                    인증 및 비밀번호 재설정 메일 발송
                  </Button>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setResetModalOpen(false)}>닫기</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default Login;
